"use client";

import { createContext, use } from "react";

import { SelfSchedulePatient } from "@/types/self-schedule";

export const PhoneAuthStep = {
  ENTER_PHONE: "ENTER_PHONE",
  ENTER_CODE: "ENTER_CODE",
  PICK_PATIENT: "PICK_PATIENT",
  PORTAL_REQUIRED: "PORTAL_REQUIRED",
  NAME: "NAME",
} as const;
export type PhoneAuthStep = (typeof PhoneAuthStep)[keyof typeof PhoneAuthStep];

export interface PhoneAuthState {
  step: PhoneAuthStep;
  phoneNumber: string;
  enteredCode: string;
  candidates: SelfSchedulePatient[];
  selectedPatient: SelfSchedulePatient | null;
  portalRequiredPatient: SelfSchedulePatient | null;
  isResolvingSession: boolean;
  isSendingCode: boolean;
  isVerifyingCode: boolean;
  isClaiming: boolean;
  isLoadingCandidates: boolean;
}

export interface PhoneAuthActions {
  submitPhone: (phone: string) => Promise<void>;
  submitCode: (code: string) => Promise<void>;
  selectPatient: (patient: SelfSchedulePatient) => Promise<void>;
  completeName: (firstName: string, lastName: string) => Promise<void>;
  requirePortalLogin: (patient: SelfSchedulePatient) => void;
  resend: () => Promise<void>;
  startNewPatient: () => void;
}

export interface PhoneAuthContextValue {
  state: PhoneAuthState;
  actions: PhoneAuthActions;
}

export const PhoneAuthContext = createContext<PhoneAuthContextValue | null>(
  null,
);

export function usePhoneAuth(): PhoneAuthContextValue {
  const ctx = use(PhoneAuthContext);
  if (!ctx) {
    throw new Error("usePhoneAuth must be used inside <AuthFlow.Phone>");
  }
  return ctx;
}

/** For components that may render outside the phone subtree (e.g. `AuthFlow.Header`). */
export function usePhoneAuthOptional(): PhoneAuthContextValue | null {
  return use(PhoneAuthContext);
}
